import type { SiteEntity } from '../../types/site';
import type { ProjectIntent, PowerAvailabilityPreferences } from '../../types/siteRag';
import { hashGenerationImpactValue } from '../generationImpact/integrity';
import { EXPLICIT_UNKNOWN_INTENT_SOURCE } from './explicitUnknown';

/** Optional fields remain absent, rather than becoming a false zero, `none`,
 * mixed workload or a design default. Explicit markers survive serialization
 * and prevent recommendations/context from silently filling these answers. */
const UNDECIDED_FIELDS = [
  'workloadProfile', 'powerRequirement', 'powerRequirementMW', 'powerBasis',
  'powerSizingMode', 'pueValue', 'powerFactor', 'annualDemandUtilizationRangePct',
  'coolingStrategy', 'bopHeatRejection', 'coolingGeneration', 'nuclearInterest',
  'reactorDesignClass', 'reactorVendorId', 'timeline', 'inServiceDate', 'priority',
  'customWeights', 'phases', 'siteSponsor', 'geographicConstraints',
  'carbonCommitment', 'carbonCommitmentNote', 'datacenterTierTarget',
  'redundancyClass', 'modularReplicableFlag', 'laborPoolPreference',
  'decommissioningPlanProvided', 'interconnectionStatus', 'fuelAccess',
  'waterSource', 'offtake', 'capacityCreditIntent', 'wasteHeatOfftake',
] as const satisfies readonly (keyof ProjectIntent)[];

/** Full replacement, not an overlay: retired scalar/phase/simulation inputs
 * and applied/promoted choice pointers cannot survive a reset. Factual
 * operator attestations and comparison-history IDs are evidence, not choices. */
export function undecidedProjectIntent(previous?: ProjectIntent): ProjectIntent {
  const factualSources = Object.fromEntries(Object.entries(previous?.fieldSource ?? {})
    .filter(([field]) => field === 'attestations' || field.startsWith('attestations.') || field === 'comparedGenerationScenarioIds'));
  return {
    screeningMode: 'recommend',
    powerRequirement: 'undecided',
    coolingStrategy: 'undecided',
    nuclearInterest: 'undecided',
    timeline: 'undecided',
    priority: 'undecided',
    bopHeatRejection: 'unknown',
    coolingGeneration: 'unknown',
    reactorDesignClass: 'undecided',
    fieldSource: { ...factualSources, ...Object.fromEntries(UNDECIDED_FIELDS.map(field => [field, EXPLICIT_UNKNOWN_INTENT_SOURCE])) },
    ...(previous?.attestations ? { attestations: { ...previous.attestations } } : {}),
    ...(previous?.comparedGenerationScenarioIds
      ? { comparedGenerationScenarioIds: [...previous.comparedGenerationScenarioIds] } : {}),
  };
}

/** BTM supply is the one Intent choice stored outside ProjectIntent. Preserve
 * the remaining report setup/factual fields exactly. An empty slot is valid. */
export function withoutBtmStrategy(previous?: PowerAvailabilityPreferences): PowerAvailabilityPreferences {
  const next = { ...(previous ?? {}) };
  delete next.btmStrategy;
  return next;
}

/** Shared canonical CAS boundary. Include the whole preference slot so a
 * concurrent factual/default edit cannot be replaced by an older reset. */
export function intentDecisionStateHash(site: Pick<SiteEntity,
  'projectIntent' | 'typedPreferencesDefaults' | 'stage2DecisionReview' | 'generationAssessmentSelection'>): string {
  return hashGenerationImpactValue({
    projectIntent: site.projectIntent ?? null,
    powerAvailabilityPreferences: site.typedPreferencesDefaults?.['power-availability'] ?? null,
    stage2DecisionReview: site.stage2DecisionReview ?? null,
    generationAssessmentSelection: site.generationAssessmentSelection ?? null,
  });
}
